import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "../../components/core/button.core";
import Input from "../../components/core/input.core";
import { useToast } from "../../../hooks/use-toast";
import {
  SocialCreatorType,
  SocialPlatforms,
} from "../../../types/models/social.models.types";
import { SocialPostType } from "../../../types/models/campaign.models.types";
import Select from "../../components/core/select.core";
import ImageUpload from "../../components/core/imageupload.core";
import Textarea from "../../components/core/textarea.core";
import PrefixTextArea from "../../components/core/prefix-textarea.core";
import { findErrorMessage, handleHttpError } from "../../../utils/utils";
import BoostIcon from "../../../assets/svgs/common/boost.svg";
import {
  type IzCreateCampaignFormBrand,
  zCreateCampaignFormBrand,
} from "../../../z/brand/create-campaign-form.brand.z";
import { useCreateCampaignHttp } from "../../hooks/http/campaigns/create-campaign.hook";
import { useUserStore } from "../../../store/user.store";
import { useCreateAssetCommonHttp } from "../../../hooks/http/common/utils/asset/create-asset.common.http";

const SocialPlatformsOptions = [
  {
    value: SocialPlatforms.instagram,
    label: "Instagram",
  },
];

const SocialPostTypeOptions = [
  {
    value: SocialPostType.REEL,
    label: "Reel",
  },
];

enum CampaignStep {
  DETAILS = 1,
  CLIPS = 2,
  BUDGET = 3,
}

export default function CreateCampaignPartial() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep] = useState<CampaignStep>(CampaignStep.DETAILS);
  const { brands } = useUserStore();

  const { execute: createCampaignHttp, fetching: creatingCampaign } =
    useCreateCampaignHttp();

  const { execute: uploadAssetHttp, fetching: uploadingAsset } =
    useCreateAssetCommonHttp();

  const form = useForm<IzCreateCampaignFormBrand>({
    resolver: zodResolver(zCreateCampaignFormBrand),
    defaultValues: {
      name: "",
      coverImage: null,
      socialPostType: SocialPostType.REEL,
      socialPlatform: SocialPlatforms.instagram,
      socialCreatorType: SocialCreatorType.meme,
      hashtags: null,
      mentions: null,
      do: null,
      dont: null,
    },
    mode: "onChange",
    shouldFocusError: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  async function uploadAsset(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await uploadAssetHttp({
      body: formData,
    });
    if (!response.success) {
      return false;
    }
    return response.data.asset;
  }

  async function createCampaign(data: IzCreateCampaignFormBrand) {
    if (!brands?.at(0)?.id) {
      return;
    }
    // Upload logo if provided
    if (data.coverImageFile.length > 0) {
      const uploadedCoverImage = await uploadAsset(data.coverImageFile[0].file);
      if (!uploadedCoverImage) {
        return toast({
          content: "Failed to upload Cover Image. Please try again",
          toastType: "error",
        });
      }

      form.setValue("coverImage", {
        id: uploadedCoverImage.id,
        name: uploadedCoverImage.name,
        url: uploadedCoverImage.url,
      });
      data.coverImage = uploadedCoverImage;
    }
    const response = await createCampaignHttp({
      body: {
        name: data.name,
        coverImage: data.coverImage?.id || null,
        socialPostType: data.socialPostType,
        socialPlatform: data.socialPlatform,
        socialCreatorType: data.socialCreatorType,
        aboutBrand: data.aboutBrand,
        hashtags: data.hashtags,
        mentions: data.mentions,
        do: data.do,
        dont: data.dont,
        brand: brands[0].id,
        platform: "solo",
        aboutCampaign: data.aboutCampaign,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "Campaign created successfully",
      toastType: "success",
    });
    navigate(`/brands/campaigns/${response.data.campaign.id}/update`);
  }

  const onSubmit = async (data: IzCreateCampaignFormBrand) => {
    console.log("Campaign data:", data);

    await createCampaign(data);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      console.log(key);
      if (key) {
        const error =
          form.formState.errors[key as keyof typeof form.formState.errors];
        const message = findErrorMessage(error) || "An error occurred";

        toast({
          content: message,
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors]);

  return (
    <FormProvider {...form}>
      <div className="flex flex-col items-center w-full h-full font-cabinetgrotesk">
        {/* Header */}
        <div className="flex justify-center items-center py-6 border-black border-b w-full font-semibold text-2xl">
          Create your Campaign
        </div>

        {/* Step Indicators */}
        <div className="flex justify-around items-center py-6 border-black border-b w-full text-base">
          <div
            className={`flex flex-col items-center gap-2 transition-opacity ${
              currentStep === CampaignStep.DETAILS
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [1] <span>Campaign details</span>
            </p>
            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.DETAILS
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>

          <div
            className={`flex flex-col items-center gap-2 transition-opacity ${
              currentStep === CampaignStep.CLIPS
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [2] <span>Campaign clips</span>
            </p>

            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.CLIPS
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>

          <div
            className={`flex flex-col items-center gap-2 transition-opacity ${
              currentStep === CampaignStep.BUDGET
                ? "opacity-100 font-semibold"
                : "opacity-30"
            }`}
          >
            <p>
              [3] <span>Campaign budget & targeting</span>
            </p>

            <div
              className={`bg-secondary rounded-full w-full h-1 ${
                currentStep === CampaignStep.BUDGET
                  ? "opacity-100 font-semibold"
                  : "opacity-30"
              } `}
            />
          </div>
        </div>

        {/* Form Content */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 px-6 py-8 w-full overflow-y-auto"
        >
          {/* Step 1: Campaign Details */}
          {currentStep === CampaignStep.DETAILS && (
            <div className="flex flex-col gap-6">
              <div className="gap-6 grid grid-cols-2">
                <Controller
                  name="coverImageFile"
                  control={control}
                  render={({ field: { value, onChange, ref } }) => (
                    <ImageUpload
                      ref={ref} // Pass ref from Controller
                      label="Upload cover image"
                      value={value?.[0]?.file || null}
                      onChange={(file) => {
                        if (file) {
                          onChange([{ file }]);
                        } else {
                          onChange([]);
                        }
                      }}
                      error={errors.coverImageFile?.message}
                      description="Max file size: 5MB (Supported formats: .png, .jpg, .jpeg)"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Campaign Name"
                      placeholder="Enter campaign name"
                      error={errors.name?.message}
                      variant="default"
                      fullWidth
                      required
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="socialPlatform"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Platform Type"
                      placeholder="Select platform type"
                      options={SocialPlatformsOptions}
                      error={errors.socialPlatform?.message}
                      fullWidth
                      value={field.value || ""}
                      onChange={(value) =>
                        field.onChange(value as SocialPlatforms)
                      }
                    />
                  )}
                />

                <Controller
                  name="socialPostType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Content Type"
                      placeholder="Select content type"
                      options={SocialPostTypeOptions}
                      error={errors.socialPostType?.message}
                      fullWidth
                      value={field.value || ""}
                      onChange={(value) =>
                        field.onChange(value as SocialPostType)
                      }
                    />
                  )}
                />

                <div className="col-span-2">
                  <Controller
                    name="aboutCampaign"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="About campaign"
                        placeholder="Enter about campaign"
                        error={errors.aboutCampaign?.message}
                        fullWidth
                        rows={5}
                        {...field}
                        value={field.value || ""}
                      />
                    )}
                  />
                </div>
                <div className="col-span-2">
                  <Controller
                    name="aboutBrand"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="About brand"
                        placeholder="Enter about brand"
                        error={errors.aboutBrand?.message}
                        fullWidth
                        rows={5}
                        {...field}
                        value={field.value || ""}
                      />
                    )}
                  />
                </div>
                <Controller
                  name="do"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label="Do's"
                      placeholder="Enter do's"
                      error={errors.do?.message}
                      fullWidth
                      rows={5}
                      {...field}
                      value={field.value || ""}
                    />
                  )}
                />
                <Controller
                  name="dont"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      label="Don't"
                      placeholder="Enter don't"
                      error={errors.dont?.message}
                      fullWidth
                      rows={5}
                      {...field}
                      value={field.value || ""}
                    />
                  )}
                />
                <PrefixTextArea
                  form={form}
                  prefix="#"
                  name="hashtags"
                  label="Mandatory hashtags"
                  placeholder="Enter hashtags"
                  required={false}
                />

                <PrefixTextArea
                  form={form}
                  prefix="@"
                  name="mentions"
                  label="Mentions"
                  placeholder="Enter mentions"
                  required={false}
                />
              </div>
            </div>
          )}
        </form>

        {/* Navigation Footer */}
        <div className="bottom-0 sticky flex justify-end items-center gap-4 bg-white px-6 py-4 border-black border-t w-full">
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="w-56 h-16"
            isLoading={creatingCampaign || uploadingAsset}
          >
            <div className="flex items-center text-nowrap">
              <BoostIcon className="mr-2" /> Create Campaign
            </div>
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
